import { useCallback, useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent, type MouseEvent, type DragEvent } from 'react';
import { createPortal } from 'react-dom';
import { ThreeDotsIcon } from '../../common/selectIcons';
import { useIsMobile } from '../../../hooks/useIsMobile';
import styles from './MaterialCard.module.css';
import { playExclusive } from '../../../utils/exclusiveAudio';
import { calculateAspectRatio, type InfoBadge } from './materialUtils';
import type { Tag } from '../../../features/tags/types';
import { Tooltip } from '../../common/Tooltip';
import HoverPillButton from '../../common/HoverPillButton';

type MaterialCardProps = {
  imageUrl?: string | null;
  contentType?: string;
  previewAlt?: string;
  infoBadges?: InfoBadge[];
  tags?: Tag[];  // タグオブジェクトの配列に変更
  isActive?: boolean;
  isMarked?: boolean;
  isDeleted?: boolean;
  checkboxAriaLabel?: string;
  checkboxChecked?: boolean;
  checkboxDisabled?: boolean;  // チェックボックスを非活性にする
  onToggleMarked?: () => void;
  onToggleActive: () => void;
  onShowDetails?: () => void;
  onDelete?: () => void;
  deleteAriaLabel?: string;
  detailLabel?: string;
  detailIconPath?: string;  // カスタム詳細アイコンパス
  detailIconRotated?: boolean;  // アイコンを180°回転
  draggable?: boolean;
  onDragStart?: (e: DragEvent<HTMLDivElement>) => void;
  onMeasure?: (metrics: { cardHeight: number; previewHeight: number }) => void;
  materialId?: string;
  deletedLabel?: string;
  deletedDateTime?: string;  // 削除日時を追加
  fixedCardHeight?: number;
  fixedPreviewHeight?: number;
  imageHeight?: number;
  width?: number;
  height?: number;
  isProtected?: boolean;  // 保護フラグ
  onToggleProtected?: () => void;  // 保護トグルハンドラー
  deleteDisabled?: boolean;  // 削除ボタンを非活性にする
  deleteDisabledTooltip?: string;  // 非活性時のツールチップ
  narrationText?: string;  // 音声素材の原稿テキスト
  audioUrl?: string | null;  // 音声再生URL
  videoUrl?: string | null;  // 動画インライン再生URL (渡すと再生ボタンを表示。ピッカー用)
  selectButtonLabel?: string;  // 渡すと常時表示の選択ボタンを出す (クリックで onToggleActive。ピッカー用)
  /** ピッカー用: タイル全体のクリックで onToggleActive を呼び、ホバーで枠線を出す (選択ボタンは出さない)。
   *  再生 / 閉じる / 詳細 / 削除 / チェックボックスは stopPropagation 済みなので対象外 */
  selectOnClick?: boolean;
};

const DEFAULT_DETAIL_LABEL = '詳細';
const DEFAULT_DELETE_ARIA = '素材を削除';
const DEFAULT_CHECKBOX_ARIA = '素材を選択';

export function MaterialCard({
  imageUrl,
  contentType,
  width,
  height,
  previewAlt,
  infoBadges,
  tags,
  isActive = false,
  isMarked = false,
  isDeleted = false,
  checkboxAriaLabel = DEFAULT_CHECKBOX_ARIA,
  checkboxChecked,
  checkboxDisabled = false,
  onToggleMarked,
  onToggleActive,
  onShowDetails,
  onDelete,
  deleteAriaLabel = DEFAULT_DELETE_ARIA,
  detailLabel = DEFAULT_DETAIL_LABEL,
  detailIconPath = '/expand_2.svg',
  detailIconRotated = false,
  draggable = false,
  onDragStart,
  onMeasure,
  materialId,
  deletedLabel = '削除済み',
  deletedDateTime,
  fixedCardHeight,
  fixedPreviewHeight,
  imageHeight = 150,
  isProtected = false,
  onToggleProtected,
  deleteDisabled = false,
  deleteDisabledTooltip,
  narrationText,
  audioUrl,
  videoUrl,
  selectButtonLabel,
  selectOnClick = false,
}: MaterialCardProps) {
  const cardRef = useRef<HTMLElement | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const trackedUrlRef = useRef(imageUrl);
  // 動画インライン再生中か (videoUrl 付きカードのみ)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const trackedVideoUrlRef = useRef(videoUrl);

  // 再生中に別素材のURLへ差し替わったら再生状態を破棄する (レンダーフェーズで同期処理)
  if (trackedVideoUrlRef.current !== videoUrl) {
    trackedVideoUrlRef.current = videoUrl;
    if (isVideoPlaying) {
      setIsVideoPlaying(false);
    }
  }

  // imageUrlが変わったらスケルトンを再表示（レンダーフェーズで同期的に処理）
  // useEffectだとonLoadイベントより後に実行され、キャッシュ済み画像のonLoadを上書きしてしまう
  if (trackedUrlRef.current !== imageUrl) {
    trackedUrlRef.current = imageUrl;
    if (imageLoaded) {
      setImageLoaded(false);
    }
  }

  const reportMetrics = useCallback(() => {
    if (!onMeasure) return;
    const cardEl = cardRef.current;
    if (!cardEl) return;
    const cardHeight = cardEl.getBoundingClientRect().height;
    const previewHeight = previewRef.current?.getBoundingClientRect().height ?? 0;
    onMeasure({ cardHeight, previewHeight });
  }, [onMeasure]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    reportMetrics();
  }, [reportMetrics]);

  const handleImageError = useCallback(() => {
    setImageLoaded(true);
    reportMetrics();
  }, [reportMetrics]);


  useEffect(() => {
    if (!onMeasure) return;
    reportMetrics();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', reportMetrics);
      return () => {
        window.removeEventListener('resize', reportMetrics);
      };
    }

    const observer = new ResizeObserver(() => {
      reportMetrics();
    });

    const cardEl = cardRef.current;
    if (cardEl) observer.observe(cardEl);
    const previewEl = previewRef.current;
    if (previewEl) observer.observe(previewEl);

    return () => {
      observer.disconnect();
    };
  }, [onMeasure, reportMetrics]);

  useEffect(() => {
    reportMetrics();
  }, [reportMetrics, imageUrl, isDeleted]);

  const handleCheckboxClick = (event: MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (isDeleted) return;
    onToggleMarked?.();
  };

  const handleDelete = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isDeleted) return;
    onDelete?.();
  };

  const handleToggleProtected = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isDeleted) return;
    onToggleProtected?.();
  };

  const handleDetails = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isDeleted) return;
    onShowDetails?.();
  };

  const handlePreviewClick = () => {
    if (isDeleted) return;
    onToggleActive();
  };

  const handlePreviewKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isDeleted) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePreviewClick();
    }
  };

  const showDetailsButton = !isDeleted && Boolean(onShowDetails);
  const hasCheckbox = !isDeleted && Boolean(onToggleMarked);
  const canDelete = !isDeleted && Boolean(onDelete);
  const showDeleteDisabled = !isDeleted && deleteDisabled && deleteDisabledTooltip;
  const hasMobileMenu = isMobile && !isDeleted && (showDetailsButton || canDelete || Boolean(onToggleProtected));

  // メニュー外クリックで閉じる
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleClickOutside = (e: Event) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (menuButtonRef.current?.contains(target)) return;
      setMobileMenuOpen(false);
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [mobileMenuOpen]);

  // 比率判定は calculateAspectRatio に集約 (Seedance 480p の非標準解像度 864x496 等も
  // 16:9/9:16 として扱われ、バッジ表示とフィルムストリップ装飾の判定が一致する)
  const normalizedRatio = (!isDeleted && width && height) ? calculateAspectRatio(width, height) : null;
  const isVideo = !!contentType?.startsWith('video/');
  const isImage = !!contentType?.startsWith('image/');

  // Check if aspect ratio is 16:9 and content is video
  const is16By9Video = normalizedRatio === '16:9' && isVideo;

  // Check if aspect ratio is 9:16 and content is video
  const is9By16Video = normalizedRatio === '9:16' && isVideo;

  // Check if aspect ratio is 9:16 and content is image
  const is9By16Image = normalizedRatio === '9:16' && isImage;

  const cardClassName = isDeleted ? `${styles.card} ${styles.cardDeleted}` : styles.card;
  const cardInnerClassNames = [styles.cardInner];
  if (isActive || isMarked) {
    cardInnerClassNames.push(styles.cardInnerSelected);
  }
  if (isDeleted) {
    cardInnerClassNames.push(styles.cardInnerDeleted);
  }
  const tileSelectable = selectOnClick && !isDeleted;
  if (tileSelectable) {
    cardInnerClassNames.push(styles.cardInnerSelectable);
  }

  const previewButtonClassName = isDeleted ? `${styles.previewButton} ${styles.previewButtonDisabled}` : styles.previewButton;

  const previewFrameClassNames = [styles.previewFrame];
  if (isDeleted) {
    previewFrameClassNames.push(styles.previewFrameDeleted);
  } else if (is16By9Video) {
    previewFrameClassNames.push(styles.previewFrameFilmStrip);
  } else if (is9By16Video) {
    previewFrameClassNames.push(styles.previewFrameFilmStripVertical);
  } else if (is9By16Image) {
    previewFrameClassNames.push(styles.previewFrameVerticalImage);
  }
  const previewFrameClassName = previewFrameClassNames.join(' ');

  const metaSectionClassNames = [styles.metaSection];
  if (isDeleted) {
    metaSectionClassNames.push(styles.metaSectionDeleted);
  } else if (is9By16Video) {
    metaSectionClassNames.push(styles.metaSectionVertical);
  } else if (is9By16Image) {
    metaSectionClassNames.push(styles.metaSectionVerticalImage);
  }
  // deletedDateTimeがある場合は高さを増やす
  if (deletedDateTime) {
    if (is9By16Video) {
      metaSectionClassNames.push(styles.metaSectionVerticalWithDateTime);
    } else if (is9By16Image) {
      metaSectionClassNames.push(styles.metaSectionVerticalImageWithDateTime);
    } else {
      metaSectionClassNames.push(styles.metaSectionWithDateTime);
    }
  }
  const metaSectionClassName = metaSectionClassNames.join(' ');

  const cardStyle = isDeleted && typeof fixedCardHeight === 'number'
    ? { height: `${Math.max(fixedCardHeight, 0)}px`, minHeight: `${Math.max(fixedCardHeight, 0)}px` }
    : undefined;

  const previewStyle = (() => {
    if (isDeleted && typeof fixedPreviewHeight === 'number') {
      return {
        height: `${Math.max(fixedPreviewHeight, 0)}px`,
        minHeight: `${Math.max(fixedPreviewHeight, 0)}px`,
      };
    }
    // 16:9動画の場合、CSS変数で画像幅を渡してフィルムストリップを可変幅に
    if (is16By9Video) {
      const imageWidth = Math.round(imageHeight * 16 / 9);
      return {
        height: `${imageHeight}px`,
        minHeight: `${imageHeight}px`,
        '--image-width': `${imageWidth}px`,
      } as React.CSSProperties;
    }
    // 9:16動画の場合、上下に固定幅21pxのフィルムストリップを配置
    if (is9By16Video) {
      const filmStripHeight = 21;
      // 9:16動画の実際の表示高さを計算（幅に合わせて縮小されるため）
      // 1:1画像のimageHeight（150px）がカード幅とほぼ同じなので、それを基準にする
      const actualImageHeight = Math.round(150 * 16 / 9);
      return {
        height: `${filmStripHeight * 2 + actualImageHeight}px`,
        minHeight: `${filmStripHeight * 2 + actualImageHeight}px`,
        '--image-height': `${actualImageHeight}px`,
      } as React.CSSProperties;
    }
    // 9:16静止画の場合、フィルムストリップなしで上詰め
    if (is9By16Image) {
      // 9:16画像の実際の表示高さを計算（幅に合わせて縮小されるため）
      const actualImageHeight = Math.round(150 * 16 / 9);
      return {
        height: `${actualImageHeight}px`,
        minHeight: `${actualImageHeight}px`,
      };
    }
    return {
      height: `${imageHeight}px`,
      minHeight: `${imageHeight}px`,
    };
  })();

  const cardDraggable = !isDeleted && draggable;
  const dragStartHandler = cardDraggable ? onDragStart : undefined;
  const previewTabIndex = isDeleted ? -1 : 0;
  const previewAriaDisabled = isDeleted ? true : undefined;

  return (
    <article
      className={cardClassName}
      ref={cardRef}
      data-material-id={materialId}
      style={cardStyle}
      draggable={cardDraggable}
      onDragStart={dragStartHandler}
    >
      {/* タイル全体クリックで選択 (ピッカー)。プレビュー側の onClick は二重発火を避けて外し、キーボード操作はプレビューに残す */}
      <div className={cardInnerClassNames.join(' ')} onClick={tileSelectable ? handlePreviewClick : undefined}>
        {(isActive || isMarked) && !isDeleted && (
          <span className={styles.selectedLabel}>選択中</span>
        )}
        <div
          role="button"
          tabIndex={previewTabIndex}
          className={previewButtonClassName}
          onClick={tileSelectable ? undefined : handlePreviewClick}
          onKeyDown={handlePreviewKeyDown}
          aria-disabled={previewAriaDisabled}
        >
          <div className={previewFrameClassName} style={previewStyle} ref={previewRef}>
            {isDeleted ? (
              <div className={styles.deletedPlaceholder}>
                <span>{deletedLabel}</span>
              </div>
            ) : imageUrl && videoUrl && isVideoPlaying ? (
              <>
                {/* インライン動画プレイヤー。クリック/キー操作はコントロール操作なので
                    カード選択 (onToggleActive) へバブリングさせない */}
                <video
                  // autoplay 属性 (メディア準備完了時に開始) と明示 play() (ユーザー操作の
                  // 有効なうちに開始要求) を併用し、環境差による再生開始漏れを防ぐ
                  ref={(el) => {
                    if (el) el.play().catch(() => {});
                  }}
                  src={videoUrl}
                  controls
                  autoPlay
                  playsInline
                  className={styles.previewVideo}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
                <button
                  type="button"
                  className={styles.videoCloseButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsVideoPlaying(false);
                  }}
                  aria-label="再生を閉じる"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </>
            ) : imageUrl ? (
              <>
                {!imageLoaded && <div className={styles.skeletonOverlay} />}
                <img
                  src={imageUrl}
                  alt={previewAlt ?? 'プレビュー'}
                  className={styles.previewImage}
                  loading="lazy"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
                {videoUrl && (
                  <button
                    type="button"
                    className={styles.videoPlayOverlay}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsVideoPlaying(true);
                    }}
                    aria-label="動画を再生"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="8,5 19,12 8,19" />
                    </svg>
                  </button>
                )}
              </>

            ) : narrationText ? (
              <div className={styles.narrationPreview}>
                <div className={styles.narrationPreviewText}>{narrationText}</div>
                {audioUrl && (
                  <>
                    <audio
                      ref={audioElRef}
                      src={audioUrl}
                      onEnded={() => setIsAudioPlaying(false)}
                      // 他の試聴に止められたときも再生中アイコンを戻す
                      onPause={() => setIsAudioPlaying(false)}
                      style={{ display: 'none' }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const el = audioElRef.current;
                        if (!el) return;
                        if (el.paused) { void playExclusive(el).catch(() => setIsAudioPlaying(false)); setIsAudioPlaying(true); }
                        else { el.pause(); setIsAudioPlaying(false); }
                      }}
                      className={styles.audioPlayButton}
                    >
                      {isAudioPlaying ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <rect x="6" y="4" width="4" height="16" rx="1" />
                          <rect x="14" y="4" width="4" height="16" rx="1" />
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <polygon points="8,5 19,12 8,19" />
                        </svg>
                      )}
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className={styles.noPreview}>No Picture</div>
            )}
            {hasCheckbox ? (
              <div className={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  aria-label={checkboxAriaLabel}
                  checked={checkboxChecked ?? isMarked}
                  disabled={checkboxDisabled}
                  onClick={handleCheckboxClick}
                  onChange={handleCheckboxChange}
                  className={checkboxDisabled ? styles.checkboxDisabled : undefined}
                />
              </div>
            ) : null}
            {/* 保護ボタン - onToggleProtectedが渡されている場合のみ表示 */}
            {onToggleProtected && (
              <button
                type="button"
                className={`${styles.protectionButton} ${isProtected ? styles.protectionButtonActive : ''}`}
                onClick={handleToggleProtected}
                aria-label={isProtected ? '保護を解除' : '素材を保護'}
              >
                <img
                  src={isProtected ? '/lock.svg' : '/lock_unlock_4.svg'}
                  alt=""
                  className={styles.protectionIcon}
                />
                <span className={styles.protectionLabel}>{isProtected ? '保護を解除' : '素材を保護'}</span>
              </button>
            )}
            {canDelete || showDeleteDisabled ? (
              <div className={styles.previewActionTopRight}>
                {canDelete ? (
                  <HoverPillButton
                    variant="danger"
                    label={deleteAriaLabel}
                    icon={<img src="/bin_1_white.svg" alt="" />}
                    onClick={handleDelete}
                    className={styles.deleteButton}
                  />
                ) : (
                  <Tooltip content={deleteDisabledTooltip ?? ''}>
                    <HoverPillButton
                      variant="danger"
                      label={deleteAriaLabel}
                      icon={<img src="/bin_1_white.svg" alt="" />}
                      disabled
                      className={`${styles.deleteButton} ${styles.deleteButtonDisabled}`}
                    />
                  </Tooltip>
                )}
              </div>
            ) : null}
            {showDetailsButton ? (
              <div className={styles.previewActionBottomRight}>
                <HoverPillButton
                  variant="default"
                  label={detailLabel}
                  icon={<img src={detailIconPath} alt="" style={detailIconRotated ? { transform: 'rotate(180deg)' } : undefined} />}
                  onClick={handleDetails}
                  className={styles.detailsButton}
                />
              </div>
            ) : null}
            {selectButtonLabel && !isDeleted ? (
              // 常時表示の選択ボタン (ピッカー用)。動画カードは下端フィルムストリップ帯へ
              // 左右中央で載せる (動画本体は帯の上に収まるため再生コントロールとは重ならない)。
              // 音声カードは中央下の再生ボタンと被るため右下のまま。
              <div className={`${styles.selectButtonContainer} ${videoUrl ? styles.selectButtonContainerCentered : ''}`}>
                <button
                  type="button"
                  className={styles.selectButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleActive();
                  }}
                >
                  {selectButtonLabel}
                </button>
              </div>
            ) : null}
          </div>
        </div>
        <footer className={metaSectionClassName}>
          <div className={styles.metaRow}>
            <div className={styles.metaBadges}>
              {infoBadges?.map((badge, index) => (
                <span
                  key={`${badge.label}-${index}`}
                  title={badge.title}
                  className={`${styles.infoBadge} ${
                    badge.variant === 'secondary'
                      ? styles.infoBadgeSecondary
                      : badge.variant === 'primary'
                        ? styles.infoBadgePrimary
                        : ''
                  }`}
                >
                  {badge.label}
                </span>
              ))}
              {tags?.map((tag) => (
                <span
                  key={typeof tag === 'object' ? tag.id : tag}
                  className={styles.tagItem}
                  style={typeof tag === 'object' && tag.color ? { backgroundColor: tag.color } : undefined}
                >
                  {typeof tag === 'object' ? tag.name : tag}
                </span>
              ))}
            </div>
            {hasMobileMenu && (
              <button
                type="button"
                ref={menuButtonRef}
                className={`${styles.mobileMenuButton} ${mobileMenuOpen ? styles.mobileMenuButtonActive : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (mobileMenuOpen) {
                    setMobileMenuOpen(false);
                  } else {
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                    setMobileMenuOpen(true);
                  }
                }}
                aria-label="メニュー"
              >
                <ThreeDotsIcon vertical />
              </button>
            )}
          </div>
          {deletedDateTime && (
            <div className={styles.deletedDateTime}>
              <div className={styles.deletedDateTimeLabel}>削除日時</div>
              <div className={styles.deletedDateTimeValue}>{deletedDateTime}</div>
            </div>
          )}
        </footer>
      </div>
      {mobileMenuOpen && menuPos && createPortal(
        <div
          ref={menuRef}
          className={styles.mobileMenu}
          style={{ top: menuPos.top, right: menuPos.right }}
        >
          {showDetailsButton && (
            <button
              type="button"
              className={styles.mobileMenuItem}
              onClick={(e) => {
                e.stopPropagation();
                onShowDetails?.();
                setMobileMenuOpen(false);
              }}
            >
              <img
                src={detailIconPath}
                alt=""
                className={`${styles.mobileMenuIcon} ${detailIconRotated ? styles.mobileMenuIconRotated : ''}`}
              />
              {detailLabel}
            </button>
          )}
          {onToggleProtected && (
            <button
              type="button"
              className={styles.mobileMenuItem}
              onClick={(e) => {
                e.stopPropagation();
                onToggleProtected();
                setMobileMenuOpen(false);
              }}
            >
              <img
                src={isProtected ? '/lock.svg' : '/lock_unlock_4.svg'}
                alt=""
                className={styles.mobileMenuIcon}
              />
              {isProtected ? '保護を解除' : '素材を保護'}
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className={`${styles.mobileMenuItem} ${styles.mobileMenuItemDanger}`}
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.();
                setMobileMenuOpen(false);
              }}
            >
              <img src="/bin_1_white.svg" alt="" className={styles.mobileMenuIcon} />
              削除
            </button>
          )}
        </div>,
        document.body
      )}
    </article>
  );
}
